import React, { useEffect, useState } from "react";
import { Badge, Button } from "reactstrap";
import {
  FaCashRegister,
  FaUndo,
  FaSignOutAlt,
  FaStore,
  FaArrowLeft,
  FaTags,
  FaBarcode,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import
 { authActions } from "../../sagas/authSlice";
import "./POSHeader.css";

import { Spinner } from "reactstrap";
import CloseShiftModal from "./CloseShiftModal";
import { shiftActions } from "../../sagas/shiftSlice";
import OpenShiftModal from "./OpenShiftModal";
import { CPMT_CUSTOMER_SITUATION_PAGE, CPMT_LABELS_PAGE, CPMT_PROMOTION_PAGE } from "../../routes/routeConstants";

const POSHeader = ({
  businessName,
  fullName,
  commercialId,
  pointOfSaleId,
  isAdmin = false,
  activeTab = "VENTES",
  onChangeTab,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const tabs = [
    { key: "VENTES", label: "Ventes", icon: FaCashRegister, theme: "primary" },
    { key: "RETOURS", label: "Retours", icon: FaUndo, theme: "info" },
    // { key: "RECEPTION", label: "Réception", icon: FaCubes, theme: "info" },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const promotionButtons = [
    { key: "CUSTOMERS", label: "Situation client", icon: FaUser, path: CPMT_CUSTOMER_SITUATION_PAGE, matchPaths: [CPMT_CUSTOMER_SITUATION_PAGE], theme: "primary" },
    {
      key: "PROMOTIONS",
      label: "Promotions",
      icon: FaTags,
      path: CPMT_PROMOTION_PAGE,
      matchPaths: [CPMT_PROMOTION_PAGE],
      theme: "promotion",
    },
    {
      key: "LABELS",
      label: "Tickets",
      icon: FaBarcode,
      path: CPMT_LABELS_PAGE,
      matchPaths: [CPMT_LABELS_PAGE],
      theme: "warning",
    },
  ];

  const { currentShift, loading: shiftLoading } = useSelector((state) => state.shift);
  const isShiftBlocked = !shiftLoading && (!currentShift || currentShift.status !== "OPENED");
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [isClosingShift, setIsClosingShift] = useState(false);

  useEffect(() => {
    if (isAdmin) return;
    // On page load, fetch open shift for the commercial
    dispatch(shiftActions.fetchOpenShift({ commercialId, pointOfSaleId }));
  }, [commercialId, pointOfSaleId, dispatch, isAdmin]);

  useEffect(() => {
    const updateScreen = () => setIsSmallScreen(window.innerWidth <= 768);
    updateScreen();
    window.addEventListener("resize", updateScreen);
    return () => window.removeEventListener("resize", updateScreen);
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: t("auth.logoutConfirmTitle"),
      text: t("auth.logoutConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("common.confirm"),
      cancelButtonText: t("common.cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(authActions.logout());
        Swal.fire({
          icon: "success",
          title: t("auth.logoutSuccess"),
          showConfirmButton: false,
          timer: 1200,
        }).then(() => navigate("/login"));
      }
    });
  };
  const handleBack = () => navigate("/modules");

  const handleCloseShift = (data) => {
    if (!currentShift?.id || isClosingShift) return;
    setIsClosingShift(true);

    // ===== VALIDATION =====
    // if (!data.closingCash || !data.closingCard) {
    //   Swal.fire({
    //     icon: "warning",
    //     title: "Champs obligatoires",
    //     text: "Veuillez saisir les montants en espèces et par carte avant de valider.",
    //   });
    //   return;
    // }

    const payload = {
      id: currentShift.id,
      data: {
        closingAmountCash: parseFloat(data.closingCash),
        closingAmountCard: parseFloat(data.closingCard),
        notes: data.notes,
      },
      onSuccess: () => {
        setIsClosingShift(false);
        setCloseModalOpen(false);

        Swal.fire({
          icon: "success",
          title: "Shift clôturé avec succès",
          text: "La caisse a été clôturée et le shift est désormais fermé.",
          showConfirmButton: false,
        });
      },
      onError: (err) => {
        setIsClosingShift(false);
        console.error("Close shift error:", err);

        Swal.fire({
          icon: "error",
          title: "Erreur lors de la clôture",
          text: "Une erreur est survenue lors de la clôture du shift. Veuillez réessayer.",
        });
      },
    };

    dispatch(shiftActions.closeShift(payload));
  };

  const handleOpenShift = () => {
    const payload = {
      data: {
        commercialId,
        pointOfSaleId, // replace with current POS ID
      },
      onSuccess: (shift) => console.log("Shift opened:", shift),
      onError: (err) => console.error("Open shift error:", err),
    };
    dispatch(shiftActions.openShift(payload));
  };

  return (
    <div className="pos-header">
      {/* LEFT */}
      <div className="pos-header__left">
        {isAdmin && (
          <button type="button" className="pos-icon-btn" onClick={handleBack} title="Retour">
            <FaArrowLeft />
          </button>
        )}
        <div className="pos-brand">
          <div className="pos-brand__logo"><FaStore /></div>
          <div className="pos-brand__meta">
            <div className="pos-brand__name">{businessName || "MATJARPOS"}</div>
            <div className="pos-brand__sub">Point de vente</div>
          </div>
        </div>
      </div>

      {/* CENTER */}
      {!isAdmin && (
        <div className="pos-header__center">
          <div className="pos-tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => onChangeTab(tab.key)}
                  className={`pos-tab ${isSmallScreen ? "icon-only" : ""} ${active ? "is-active" : ""} theme-${tab.theme}`}
                  title={tab.label}
                >
                  <span className="pos-tab__icon"><Icon /></span>
                  {!isSmallScreen && <span className="pos-tab__label">{tab.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pos-header__actions">
        <div className="pos-promo-links">
          {promotionButtons.map((button) => {
            const Icon = button.icon;
            const isActive = (button.matchPaths || []).some((path) => location.pathname === path);

            return (
              <button
                key={button.key}
                type="button"
                onClick={() => navigate(button.path)}
                className={`pos-tab pos-tab--promo ${button.key === "CUSTOMERS" ? "pos-tab--customer" : ""} ${isSmallScreen ? "icon-only" : ""} ${isActive ? "is-active" : ""} theme-${button.theme}`}
                title={button.label}
                aria-label={button.label}
              >
                <span className="pos-tab__icon"><Icon /></span>
                {(!isSmallScreen || button.key === "CUSTOMERS") && <span className="pos-tab__label">{button.label}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT */}
      <div className="pos-header__right d-flex align-items-center">
        {/* SHIFT INFO */}
        {!shiftLoading && currentShift && (
          <div
            className="d-flex align-items-center me-3 p-1 px-2 rounded"
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              fontSize: "0.85rem",
              height: "42px",
              minWidth: "180px",
            }}
          >
            <div className="d-flex flex-column justify-content-center me-2">
              <div>{currentShift.shiftCode}</div>
              <div style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "6px" }}>
                <Badge
                  color={
                    currentShift.status === "OPENED" ? "success" :
                      currentShift.status === "CLOSED" ? "danger" :
                        currentShift.status === "VALIDATED" ? "primary" :
                          "secondary"
                  }
                  style={{ fontSize: "0.7rem", padding: "0.25em 0.4em" }}
                >
                  {currentShift.status === "OPENED" ? "Ouvert" :
                    currentShift.status === "CLOSED" ? "Clos" :
                      currentShift.status === "VALIDATED" ? "Validé" :
                        currentShift.status}
                </Badge>
                <span>{currentShift.openingTime}</span>
              </div>
            </div>
            <Button
              color="danger"
              size="sm"
              onClick={() => setCloseModalOpen(true)}
              style={{ height: "28px", fontSize: "0.75rem" }}
              title="Clore le shift"
            >
              X
            </Button>
          </div>
        )}
        {shiftLoading && <Spinner size="sm" className="me-3" />}

        {/* USER INFO */}
        <div className="pos-user me-2">
          <div className="pos-user__avatar">{(fullName || "U").trim().charAt(0).toUpperCase()}</div>
          <div className="pos-user__info">
            <div className="pos-user__name">{fullName || "Utilisateur"}</div>
            <div className="pos-user__role">{isAdmin ? "Admin" : "Caissier"}</div>
          </div>
        </div>

        <button
          type="button"
          className="pos-logout-btn"
          onClick={handleLogout}
          title="Déconnexion"
        >
          <FaSignOutAlt />
        </button>
      </div>


      <CloseShiftModal
        isOpen={closeModalOpen}
        toggle={() => setCloseModalOpen(false)}
        currentShift={currentShift}
        onCloseShift={handleCloseShift}
        isSubmitting={isClosingShift}
      />

      {/* Show Open Shift Modal if no active shift */}
      {!isAdmin && isShiftBlocked && (
        <OpenShiftModal
          commercialId={commercialId}
          onOpenShift={handleOpenShift}
          onLogout={handleLogout}
        />
      )}


    </div>
  );
};


export default POSHeader;
